import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Header,
  SpaceBetween,
  Box,
  Button,
  ButtonDropdown,
  Table,
  TextFilter,
  Container,
  Badge,
  FormField,
  Modal,
  Input,
  Spinner,
  StatusIndicator,
  Pagination,
  Checkbox,
  Alert,
  Flashbar,
  FlashbarProps,
} from "@cloudscape-design/components";
import { FeaturePermission } from "./types";
import { PermissionRole } from "../../redux/types/permissionTypes";
import { RootState } from "../../redux/reducers/rootReducer";
import { getPermissionRolesRequest, getPermissionMeRequest, upsertPermissionRoleRequest, changePermissionRoleStatusRequest } from "../../redux/actions/permissionActions";
import { PAGE_SIZE, SYSTEM } from "./constants";
import { getRoleBadgeColor, getFeatureLabel, buildActionIdToNameMap, formatDateTime } from "./helpers";
import { usePermission } from "../../hooks/usePermission";


export interface RoleDefinitionsTabProps {
  selectedSystem: string;
}

const RoleDefinitionsTab: React.FC<RoleDefinitionsTabProps> = ({ selectedSystem }) => {
  const dispatch = useDispatch();
  const { data: rolesData, status, error } = useSelector((state: RootState) => state.permission.roles);
  const upsertRole = useSelector((state: RootState) => state.permission.upsertRole);
  const changeRoleStatusState = useSelector((state: RootState) => state.permission.changeRoleStatus);
  const constantsData = useSelector((state: RootState) => state.permission.constants.data);

  const assignmentsData = useSelector((state: RootState) => state.permission.assignments.data);
  const meData = useSelector((state: RootState) => state.permission.me.data);
  const isSystemAdmin = !!meData?.is_system_admin?.[SYSTEM];
  const isAdmin = !!meData?.assignments?.some(
    (a) => a.system === SYSTEM && a.roles.includes("Administrator")
  );
  const currentUser = meData?.user || "";

  const { hasPermission } = usePermission();


  // Build the full feature/action list for the selected system from constants API.
  // Uses API keys directly — no mapping needed.
  // Uses hasPermission() to determine which actions are disabled for the current user.
  const defaultPermissions: FeaturePermission[] = useMemo(() => {
    const system = selectedSystem === "All" ? "MSA" : selectedSystem;
    const featureActions = constantsData?.feature_actions?.[system];
    if (!featureActions) return [];

    return Object.entries(featureActions).map(([apiFeature, actions]) => {
      return {
        feature: apiFeature,
        featureName: getFeatureLabel(apiFeature),
        actions: actions.map((item) => {
          const [apiActionId, actionName] = Object.entries(item)[0];
          const hasAction = hasPermission(`${apiFeature}.${apiActionId}`);
          return { actionId: apiActionId, action: actionName, checked: false, disabled: !hasAction };
        }),
      };
    });
  }, [constantsData, selectedSystem, hasPermission]);

  const actionIdToName = useMemo(() => buildActionIdToNameMap(constantsData?.feature_actions), [constantsData]);

  const allRoles = rolesData || [];
  const roles = selectedSystem === "All"
    ? allRoles.filter((r) => r.system === SYSTEM)
    : allRoles.filter((r) => r.system === selectedSystem);

  const [selectedRoles, setSelectedRoles] = useState<PermissionRole[]>([]);
  const [roleCurrentPage, setRoleCurrentPage] = useState(1);
  const [roleFilterText, setRoleFilterText] = useState("");
  const [sortingColumn, setSortingColumn] = useState<{ sortingField?: string }>({});
  const [sortingDescending, setSortingDescending] = useState(false);

  // Role Form Modal (unified for add/edit)
  const [roleModalVisible, setRoleModalVisible] = useState(false);
  const [roleModalMode, setRoleModalMode] = useState<"add" | "edit">("add");
  const [editingRoleDef, setEditingRoleDef] = useState<PermissionRole | null>(null);
  const [modalRoleName, setModalRoleName] = useState("");
  const [modalDescription, setModalDescription] = useState("");
  const [modalPermissions, setModalPermissions] = useState<FeaturePermission[]>(defaultPermissions);
  const [submitting, setSubmitting] = useState(false);

  const [upsertError, setUpsertError] = useState<string | null>(null);
  const [flashItems, setFlashItems] = useState<FlashbarProps.MessageDefinition[]>([]);
  const flashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showFlash = useCallback((type: "success" | "error", content: string) => {
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    setFlashItems([{ type, content, dismissible: true, onDismiss: () => setFlashItems([]) }]);
    flashTimerRef.current = setTimeout(() => setFlashItems([]), 3000);
  }, []);

  // Status Change Modal
  const [statusChangeVisible, setStatusChangeVisible] = useState(false);
  const [statusChangeAction, setStatusChangeAction] = useState<"active" | "inactive">("active");
  const [statusChangeLoading, setStatusChangeLoading] = useState(false);

  // Delete Role Modal
  const [deleteRoleVisible, setDeleteRoleVisible] = useState(false);

  const affectedUsersForDelete = useMemo(() => {
    if (!deleteRoleVisible || selectedRoles.length === 0 || !assignmentsData?.assignments) return [];
    const roleNames = new Set(selectedRoles.map((r) => r.role_name));
    const userSet = new Set<string>();
    assignmentsData.assignments.forEach((a) => {
      if (a.roles.some((r) => roleNames.has(r))) {
        userSet.add(a.entity);
      }
    });
    return Array.from(userSet);
  }, [deleteRoleVisible, selectedRoles, assignmentsData]);

  useEffect(() => {
    if (status === "idle") {
      dispatch(getPermissionRolesRequest(SYSTEM));
    }
  }, [dispatch, status]);

  useEffect(() => {
    if (upsertRole.status === "succeeded") {
      if (roleModalVisible) {
        setSubmitting(false);
        setUpsertError(null);
        if (roleModalMode === "edit") setSelectedRoles([]);
        showFlash("success", roleModalMode === "add" ? "Role created successfully." : "Role updated successfully.");
        closeRoleModal();
        dispatch(getPermissionRolesRequest(SYSTEM));
        dispatch(getPermissionMeRequest(SYSTEM));
      }
    } else if (upsertRole.status === "failed") {
      if (roleModalVisible) {
        setSubmitting(false);
        setUpsertError(upsertRole.error);
      }
    }
  }, [upsertRole.status]);

  useEffect(() => {
    if (changeRoleStatusState.status === "succeeded") {
      if (statusChangeVisible) {
        setStatusChangeLoading(false);
        setStatusChangeVisible(false);
        showFlash("success", "Role status updated successfully.");
        setSelectedRoles([]);
        dispatch(getPermissionRolesRequest(SYSTEM));
        dispatch(getPermissionMeRequest(SYSTEM));
      } else if (deleteRoleVisible) {
        setDeleteRoleVisible(false);
        setSelectedRoles([]);
        showFlash("success", "Role(s) deleted successfully.");
        dispatch(getPermissionRolesRequest(SYSTEM));
        dispatch(getPermissionMeRequest(SYSTEM));
      }
    } else if (changeRoleStatusState.status === "failed") {
      if (statusChangeVisible) {
        setStatusChangeLoading(false);
        setStatusChangeVisible(false);
        showFlash("error", changeRoleStatusState.error || "Failed to update role status.");
      } else if (deleteRoleVisible) {
        setDeleteRoleVisible(false);
        showFlash("error", changeRoleStatusState.error || "Failed to delete role.");
      }
    }
  }, [changeRoleStatusState.status]);

  useEffect(() => {
    if (roleModalVisible) return;
    setModalPermissions(defaultPermissions.map((p) => ({ ...p, actions: p.actions.map((a) => ({ ...a })) })));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultPermissions]);

  const openAddRoleModal = () => {
    setRoleModalMode("add");
    setModalRoleName("");
    setModalDescription("");
    setModalPermissions(defaultPermissions.map((p) => ({ ...p, actions: p.actions.map((a) => ({ ...a })) })));
    setEditingRoleDef(null);
    setUpsertError(null);
    setRoleModalVisible(true);
  };

  const openEditRoleModal = () => {
    if (selectedRoles.length === 1) {
      const role = selectedRoles[0];
      setRoleModalMode("edit");
      setEditingRoleDef(role);
      setUpsertError(null);
      setModalRoleName(role.role_name);
      setModalDescription(role.description || "");
      const isAllAccess = role.permissions.length === 1 && role.permissions[0].feature === "All" && role.permissions[0].actions.includes("All");
      if (isAllAccess) {
        setModalPermissions(defaultPermissions.map((dp) => ({
          feature: dp.feature,
          featureName: dp.featureName,
          actions: dp.actions.map((a) => ({ ...a, checked: true })),
        })));
      } else {
        // Map role.permissions by feature name for direct matching with defaultPermissions
        const rolePermMap = new Map(
          role.permissions.map((p) => [p.feature, new Set(p.actions)])
        );
        setModalPermissions(defaultPermissions.map((dp) => {
          const roleActions = rolePermMap.get(dp.feature);
          return {
            feature: dp.feature,
            featureName: dp.featureName,
            actions: dp.actions.map((a) => ({
              ...a,
              checked: roleActions?.has(a.actionId) ?? false,
            })),
          };
        }));
      }
      setRoleModalVisible(true);
    }
  };

  const closeRoleModal = () => {
    setRoleModalVisible(false);
    setEditingRoleDef(null);
  };

  const isAddMode = roleModalMode === "add";

  // Detect whether the edit modal has unsaved changes compared to the original role
  const hasChanges = useMemo(() => {
    if (isAddMode) return true;
    if (!editingRoleDef) return false;
    if (modalDescription !== (editingRoleDef.description || "")) return true;
    const isOriginalAllAccess = editingRoleDef.permissions.length === 1
      && editingRoleDef.permissions[0].feature === "All"
      && editingRoleDef.permissions[0].actions.includes("All");
    if (isOriginalAllAccess) {
      return modalPermissions.some((fp) => fp.actions.some((a) => !a.checked));
    }
    const originalPermMap = new Map(
      editingRoleDef.permissions.map((p) => [p.feature, new Set(p.actions)])
    );
    return modalPermissions.some((fp) => {
      const originalActions = originalPermMap.get(fp.feature);
      return fp.actions.some((a) => {
        const wasChecked = originalActions?.has(a.actionId) ?? false;
        return a.checked !== wasChecked;
      });
    });
  }, [isAddMode, editingRoleDef, modalDescription, modalPermissions]);

  // Action-to-tab mapping: selecting a child action auto-checks the parent tab action;
  // unchecking a parent tab action auto-unchecks its child actions.
  const actionToTabMap: Record<string, string> = {
    pm_assign_role: "pm_view_users_roles",
    pm_add_role: "pm_view_roles_definitions",
  };
  const tabToChildrenMap: Record<string, string[]> = {
    pm_view_users_roles: ["pm_assign_role"],
    pm_view_roles_definitions: ["pm_add_role"],
  };

  // Toggle action checkbox with view_all (select-all) linkage logic
  const toggleAction = (featureIdx: number, actIdx: number, checked: boolean) => {
    const updated = [...modalPermissions];
    const updatedActions = [...updated[featureIdx].actions];
    const viewAllIdx = updatedActions.findIndex((a) => a.actionId.endsWith("_view_all"));

    if (actIdx === viewAllIdx) {
      // Clicked view_all — select/deselect all enabled actions in this row
      const enabledActions = updatedActions.filter((a) => !a.disabled);
      const allChecked = enabledActions.every((a) => a.checked);
      const newChecked = !allChecked;
      for (let i = 0; i < updatedActions.length; i++) {
        if (!updatedActions[i].disabled) {
          updatedActions[i] = { ...updatedActions[i], checked: newChecked };
        }
      }
    } else {
      updatedActions[actIdx] = { ...updatedActions[actIdx], checked };
      // When checking a regular action, ensure view_all is also checked
      if (checked && viewAllIdx >= 0 && !updatedActions[viewAllIdx].checked) {
        updatedActions[viewAllIdx] = { ...updatedActions[viewAllIdx], checked: true };
      }
      // When checking a child action, auto-check its parent tab action
      if (checked) {
        const parentTabAction = actionToTabMap[updatedActions[actIdx].actionId];
        if (parentTabAction) {
          const parentIdx = updatedActions.findIndex((a) => a.actionId === parentTabAction);
          if (parentIdx >= 0 && !updatedActions[parentIdx].checked) {
            updatedActions[parentIdx] = { ...updatedActions[parentIdx], checked: true };
          }
        }
      }
      // When unchecking a tab action, auto-uncheck its child actions
      if (!checked) {
        const children = tabToChildrenMap[updatedActions[actIdx].actionId];
        if (children) {
          children.forEach((childId) => {
            const childIdx = updatedActions.findIndex((a) => a.actionId === childId);
            if (childIdx >= 0 && updatedActions[childIdx].checked) {
              updatedActions[childIdx] = { ...updatedActions[childIdx], checked: false };
            }
          });
        }
      }
    }

    updated[featureIdx] = { ...updated[featureIdx], actions: updatedActions };
    setModalPermissions(updated);
  };

  const convertPermissionsToApiFormat = (permissions: FeaturePermission[]) => {
    const result = permissions
      .map((p) => ({
        feature: p.feature,
        actions: p.actions.filter((a) => a.checked).map((a) => a.actionId),
      }))
      .filter((p) => p.actions.length > 0);

    const coversAll = permissions.every((p) =>
      p.actions.every((a) => result.find((r) => r.feature === p.feature)?.actions.includes(a.actionId))
    );
    if (coversAll) {
      return [{ feature: "All", actions: ["All"] }];
    }

    return result;
  };

  const handleSubmitRole = () => {
    const apiPermissions = convertPermissionsToApiFormat(modalPermissions);
    setSubmitting(true);
    setUpsertError(null);
    if (roleModalMode === "add") {
      dispatch(
        upsertPermissionRoleRequest({
          system: selectedSystem === "All" ? "MSA" : selectedSystem,
          role_name: modalRoleName.trim(),
          description: modalDescription.trim(),
          permissions: apiPermissions,
        })
      );
    } else if (editingRoleDef) {
      dispatch(
        upsertPermissionRoleRequest({
          role_id: editingRoleDef.role_id,
          description: modalDescription.trim(),
          permissions: apiPermissions,
          status: editingRoleDef.status,
          version: editingRoleDef.version || 1,
        })
      );
    }
  };

  const rolesToUpdate = useMemo(() => {
    return selectedRoles.filter((r) => r.status !== statusChangeAction);
  }, [selectedRoles, statusChangeAction]);

  const statusSkippedCount = selectedRoles.length - rolesToUpdate.length;

  const handleStatusChange = () => {
    if (rolesToUpdate.length === 0) return;
    setStatusChangeLoading(true);
    dispatch(
      changePermissionRoleStatusRequest({
        type: statusChangeAction,
        ids: rolesToUpdate.map((r) => r.role_id),
        versions: rolesToUpdate.map((r) => r.version || 1),
      })
    );
  };

  const hasRoleSelection = selectedRoles.length > 0;

  const sortedRoles = useMemo(() => {
    const sorted = [...roles].sort((a, b) => {
      if (a.type === "system" && b.type !== "system") return -1;
      if (a.type !== "system" && b.type === "system") return 1;
      if (!sortingColumn.sortingField) return 0;
      const field = sortingColumn.sortingField as keyof PermissionRole;
      const aVal = a[field] ?? "";
      const bVal = b[field] ?? "";
      if (aVal < bVal) return -1;
      if (aVal > bVal) return 1;
      return 0;
    });
    return sortingDescending ? sorted.reverse() : sorted;
  }, [roles, sortingColumn, sortingDescending]);

  const filteredRoles = roleFilterText
    ? sortedRoles.filter((r) => {
        const lower = roleFilterText.toLowerCase();
        return r.role_name.toLowerCase().includes(lower) ||
          (r.description || "").toLowerCase().includes(lower) ||
          r.type.toLowerCase().includes(lower) ||
          r.system.toLowerCase().includes(lower);
      })
    : sortedRoles;

  if (status === "loading") {
    return (
      <div className="role-definitions-section">
        <Container>
          <Box textAlign="center" padding={{ vertical: "xl" }}>
            <Spinner size="large" />
            <Box variant="p" margin={{ top: "s" }}>Loading roles...</Box>
          </Box>
        </Container>
      </div>
    );
  }

  if (status === "failed") {
    return (
      <div className="role-definitions-section">
        <Container>
          <Box textAlign="center" padding={{ vertical: "xl" }}>
            <StatusIndicator type="error">
              {error || "Failed to load roles"}
            </StatusIndicator>
            <Box margin={{ top: "s" }}>
              <Button onClick={() => dispatch(getPermissionRolesRequest(SYSTEM))}>Retry</Button>
            </Box>
          </Box>
        </Container>
      </div>
    );
  }

  return (
    <div className="role-definitions-section" data-testid="role-definitions-section">
      {flashItems.length > 0 && <Flashbar items={flashItems} />}
      <SpaceBetween size="l">
        <Container
          header={
            <Header
              variant="h2"
              description="Define roles and their feature access permissions. Role determines what actions a user can perform."
              counter={`(${filteredRoles.length})`}
              actions={
                <SpaceBetween direction="horizontal" size="xs">
                  <Button data-testid="role-add-button" iconName="add-plus" disabled={!hasPermission("permission_management.pm_add_role")} onClick={openAddRoleModal}>Add Role</Button>
                  <Button data-testid="role-edit-button" disabled={selectedRoles.length !== 1} onClick={openEditRoleModal}>Edit</Button>
                  <ButtonDropdown
                    data-testid="role-actions-dropdown"
                    items={[
                      { id: "active", text: "Activate", disabled: !hasRoleSelection || selectedRoles.every((r) => r.status === "active") },
                      { id: "inactive", text: "Inactivate", disabled: !hasRoleSelection || selectedRoles.every((r) => r.status === "inactive") },
                      { id: "deleted", text: "Delete", disabled: !hasRoleSelection },
                    ]}
                    onItemClick={({ detail }) => {
                      if (detail.id === "active") { setStatusChangeAction("active"); setStatusChangeVisible(true); }
                      else if (detail.id === "inactive") { setStatusChangeAction("inactive"); setStatusChangeVisible(true); }
                      else if (detail.id === "deleted") { setDeleteRoleVisible(true); }
                    }}
                    disabled={!hasRoleSelection}
                  >
                    Actions
                  </ButtonDropdown>
                </SpaceBetween>
              }
            >
              Roles
            </Header>
          }
        >
          <Table
            data-testid="roles-table"
            wrapLines
            resizableColumns
            items={filteredRoles.slice((roleCurrentPage - 1) * PAGE_SIZE, roleCurrentPage * PAGE_SIZE)}
            selectedItems={selectedRoles}
            onSelectionChange={({ detail }) => setSelectedRoles(detail.selectedItems)}
            selectionType="multi"
            isItemDisabled={(item) => item.type === "system" || (!isSystemAdmin && !isAdmin && item.creator !== currentUser)}
            sortingColumn={sortingColumn}
            sortingDescending={sortingDescending}
            onSortingChange={({ detail }) => {
              setSortingColumn(detail.sortingColumn);
              setSortingDescending(detail.isDescending ?? false);
              setRoleCurrentPage(1);
            }}
            trackBy="role_id"
            columnDefinitions={[
              ...(selectedSystem === "All" ? [{
                id: "system",
                header: "System",
                cell: (item: PermissionRole) => (
                  <span className={item.status === "inactive" ? "row-inactive" : ""}>
                    <Badge color={item.system === "MSA" ? "blue" : item.system === "Atlas" ? "green" : "grey"}>
                      {item.system}
                    </Badge>
                  </span>
                ),
                width: 110,
              }] : []),
              {
                id: "roleName",
                header: "Role",
                cell: (item) => (
                  <span className={`cell-wrap ${item.status === "inactive" ? "row-inactive" : ""}`}>
                    <Badge color={getRoleBadgeColor(item.role_name)}>{item.role_name}</Badge>
                  </span>
                ),
                sortingField: "role_name",
                width: 200,
              },
              {
                id: "type",
                header: "Type",
                cell: (item) => (
                  <span className={item.status === "inactive" ? "row-inactive" : ""}>
                    <span className={`role-type-tag ${item.type}`}>
                      {item.type === "system" ? "System" : "Custom"}
                    </span>
                  </span>
                ),
                sortingField: "type",
                width: 120,
              },
              {
                id: "permissions",
                header: "Features & Actions",
                cell: (item) => {
                  if (item.permissions.length === 0) {
                    return <Box color="text-body-secondary">No permissions</Box>;
                  }
                  const isAllAccess = item.permissions.length === 1 && item.permissions[0].feature === "All" && item.permissions[0].actions.includes("All");
                  if (isAllAccess) {
                    return (
                      <span className={item.status === "inactive" ? "row-inactive" : ""}>
                        <div className="permission-features-grid">
                          <div className="permission-feature-row">
                            <span className="permission-feature-name permission-tag-all">All Features</span>
                            <span className="permission-actions-cell">
                              <span className="permission-action-tag permission-tag-all"> All Actions</span>
                            </span>
                          </div>
                        </div>
                      </span>
                    );
                  }
                  return (
                    <span className={item.status === "inactive" ? "row-inactive" : ""}>
                      <div className="permission-features-grid">
                        {item.permissions.map((perm) => (
                          <div key={perm.feature} className="permission-feature-row">
                            <span className="permission-feature-name permission-tag-feature">{getFeatureLabel(perm.feature)}</span>
                            <span className="permission-actions-cell">
                              {perm.actions.filter((action) => !action.endsWith("_view_all")).map((action) => (
                                <span key={action} className="permission-action-tag permission-tag-action">{actionIdToName.get(action) || action}</span>
                              ))}
                            </span>
                          </div>
                        ))}
                      </div>
                    </span>
                  );
                },
                width: 340,
              },
              {
                id: "description",
                header: "Description",
                cell: (item) => (
                  <span className={item.status === "inactive" ? "row-inactive" : ""}>
                    <Box color="text-body-secondary" fontSize="body-s">{item.description}</Box>
                  </span>
                ),
                width:180,
              },
              {
                id: "creator",
                header: "Creator",
                cell: (item) => (
                  <span className={item.status === "inactive" ? "row-inactive" : ""}>
                    {item.creator}
                  </span>
                ),
                sortingField: "creator",
                width: 180,
              },
              {
                id: "updated_by",
                header: "Updater",
                cell: (item) => (
                  <span className={item.status === "inactive" ? "row-inactive" : ""}>
                    {item.updated_by}
                  </span>
                ),
                sortingField: "updated_by",
                width: 180,
              },
              {
                id: "updated_at",
                header: "Updated DateTime",
                cell: (item) => (
                  <span className={item.status === "inactive" ? "row-inactive" : ""}>
                    {formatDateTime(item.updated_at)}
                  </span>
                ),
                sortingField: "updated_at",
                width: 180,
              },
              {
                id: "status",
                header: "Status",
                cell: (item) => (
                  <StatusIndicator type={item.status === "active" ? "success" : "stopped"}>
                    {item.status === "active" ? "Active" : "Inactive"}
                  </StatusIndicator>
                ),
                sortingField: "status",
                width: 120,
              },
            ]}
            filter={
              <TextFilter
                data-testid="roles-filter"
                filteringText={roleFilterText}
                filteringPlaceholder="Search by role name, type, or description..."
                onChange={({ detail }) => { setRoleFilterText(detail.filteringText); setRoleCurrentPage(1); }}
              />
            }
            pagination={
              <Pagination
                data-testid="roles-pagination"
                currentPageIndex={roleCurrentPage}
                pagesCount={Math.max(1, Math.ceil(filteredRoles.length / PAGE_SIZE))}
                onChange={({ detail }) => setRoleCurrentPage(detail.currentPageIndex)}
              />
            }
            empty={
              <Box textAlign="center" color="inherit">
                <b>No roles defined</b>
              </Box>
            }
          />
        </Container>
      </SpaceBetween>
      {/* ── Create or Edit Role Modal ── */}
      <Modal
        data-testid="role-modal"
        visible={roleModalVisible}
        onDismiss={closeRoleModal}
        header={isAddMode ? "Add Role" : `Edit Role — ${modalRoleName}`}
        size="large"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button data-testid="role-modal-cancel" variant="link" onClick={closeRoleModal}>Cancel</Button>
              <Button data-testid="role-modal-submit" variant="primary" disabled={(isAddMode && !modalRoleName.trim()) || !modalPermissions.some((p) => p.actions.some((a) => a.checked)) || !hasChanges || submitting} loading={submitting} onClick={handleSubmitRole}>
                {isAddMode ? "Create" : "Save"}
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="l">
          {upsertError && (
            <Alert type="error" dismissible onDismiss={() => setUpsertError(null)}>
              {upsertError}
            </Alert>
          )}
          {!isAddMode && !hasChanges && (
            <Alert type="warning">No changes detected.</Alert>
          )}
          <div className="role-form-row">
            <FormField label={isAddMode ? <span>Role Name <span className="required-asterisk">*</span></span> : "Role Name"}>
              <Input
                data-testid="role-name-input"
                value={modalRoleName}
                onChange={({ detail }) => {
                  const sanitized = detail.value.replace(/[^a-zA-Z0-9@._\-+\s]/g, "").slice(0, 100);
                  setModalRoleName(sanitized);
                }}
                placeholder="e.g. Update Program"
                disabled={!isAddMode}
              />
            </FormField>
            <FormField label="Description">
              <Input
                data-testid="role-description-input"
                value={modalDescription}
                onChange={({ detail }) => {
                  const sanitized = detail.value.replace(/[^a-zA-Z0-9@._\-+\s,;:()'/]/g, "").slice(0, 300);
                  setModalDescription(sanitized);
                }}
                placeholder="Brief description of this role's purpose"
              />
            </FormField>
          </div>
          <FormField label={<span>Features &amp; Actions <span className="required-asterisk">*</span></span>} stretch>
            <div className="perm-chip-panel" data-testid="role-permissions-panel">
              <div className="perm-chip-row perm-chip-header">
                <Checkbox
                  checked={modalPermissions.every((p) => p.actions.filter((a) => !a.disabled).every((a) => a.checked))}
                  indeterminate={
                    modalPermissions.some((p) => p.actions.some((a) => !a.disabled && a.checked)) &&
                    !modalPermissions.every((p) => p.actions.filter((a) => !a.disabled).every((a) => a.checked))
                  }
                  onChange={({ detail }) => {
                    setModalPermissions(modalPermissions.map((p) => ({
                      ...p, actions: p.actions.map((a) => a.disabled ? a : { ...a, checked: detail.checked }),
                    })));
                  }}
                >
                  <span className="perm-chip-header-label">All Features</span>
                </Checkbox>
                <div className="perm-chip-actions">
                  {/* <Checkbox
                    checked={modalPermissions.every((p) => p.actions.filter((a) => !a.disabled && !a.actionId.endsWith("_view_all")).every((a) => a.checked))}
                    indeterminate={
                      modalPermissions.some((p) => p.actions.some((a) => !a.disabled && !a.actionId.endsWith("_view_all") && a.checked)) &&
                      !modalPermissions.every((p) => p.actions.filter((a) => !a.disabled && !a.actionId.endsWith("_view_all")).every((a) => a.checked))
                    }
                    onChange={({ detail }) => {
                      setModalPermissions(modalPermissions.map((p) => ({
                        ...p, actions: p.actions.map((a) => {
                          if (a.disabled) return a;
                          if (a.actionId.endsWith("_view_all")) {
                            return detail.checked ? { ...a, checked: true } : a;
                          }
                          return { ...a, checked: detail.checked };
                        }),
                      })));
                    }}
                  >
                    <span className="perm-chip-header-label">All Actions</span>
                  </Checkbox> */}
                  <span className="perm-chip-header-label">All Actions</span>
                </div>
              </div>
              {modalPermissions.map((perm, featureIdx) => {
                const visibleActions = perm.actions.filter((a) => !a.disabled);
                if (visibleActions.length === 0) return null;
                const viewAllIdx = perm.actions.findIndex((a) => a.actionId.endsWith("_view_all"));
                const viewAllAction = viewAllIdx >= 0 && !perm.actions[viewAllIdx].disabled ? perm.actions[viewAllIdx] : null;
                const onlyHasViewAll = visibleActions.length === 1 && visibleActions[0].actionId.endsWith("_view_all");
                return (
                  <div key={perm.feature} className="perm-chip-row">
                    <span className="perm-chip-feature">
                      {viewAllAction ? (
                        <Checkbox
                          checked={viewAllAction.checked}
                          onChange={({ detail }) => toggleAction(featureIdx, viewAllIdx, detail.checked)}
                        >
                          {perm.featureName}
                        </Checkbox>
                      ) : (
                        perm.featureName
                      )}
                    </span>
                    <div className="perm-chip-actions">
                      {perm.actions.map((act, actIdx) => {
                        if (act.disabled) return null;
                        if (act.actionId.endsWith("_view_all") && !onlyHasViewAll) return null;
                        return (
                          <span
                            key={act.actionId}
                            className="perm-chip-action-item"
                            onClick={() => toggleAction(featureIdx, actIdx, !act.checked)}
                          >
                            <Checkbox
                              checked={act.checked}
                              onChange={({ detail }) => toggleAction(featureIdx, actIdx, detail.checked)}
                            >
                              {act.action}
                            </Checkbox>
                          </span>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </FormField>
        </SpaceBetween>
      </Modal>

      {/* ── Status Change Modal ── */}
      <Modal
        data-testid="role-status-modal"
        visible={statusChangeVisible}
        onDismiss={() => !statusChangeLoading && setStatusChangeVisible(false)}
        header={`Confirm ${statusChangeAction === "active" ? "Activate" : "Inactivate"}`}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button data-testid="role-status-cancel" variant="link" disabled={statusChangeLoading} onClick={() => setStatusChangeVisible(false)}>Cancel</Button>
              <Button data-testid="role-status-confirm" variant="primary" loading={statusChangeLoading} disabled={rolesToUpdate.length === 0} onClick={handleStatusChange}>Confirm</Button>
            </SpaceBetween>
          </Box>
        }
      >
        <Box variant="p">
          Are you sure you want to {statusChangeAction === "active" ? "activate" : "inactivate"}{" "}
          {rolesToUpdate.length === 1
            ? <>the role <strong>{rolesToUpdate[0]?.role_name}</strong></>
            : <><strong>{rolesToUpdate.length}</strong> role(s)</>
          }?
          {statusSkippedCount > 0 && (
            <Box variant="p" color="text-body-secondary" padding={{ top: "xs" }}>
              Note: {statusSkippedCount} role(s) already {statusChangeAction} will be skipped.
            </Box>
          )}
        </Box>
      </Modal>

      {/* ── Delete Role Modal ── */}
      <Modal
        data-testid="role-delete-modal"
        visible={deleteRoleVisible}
        onDismiss={() => changeRoleStatusState.status !== "loading" && setDeleteRoleVisible(false)}
        header="Delete Role"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button data-testid="role-delete-cancel" variant="link" disabled={changeRoleStatusState.status === "loading"} onClick={() => setDeleteRoleVisible(false)}>Cancel</Button>
              <Button data-testid="role-delete-confirm" variant="primary" loading={changeRoleStatusState.status === "loading"} onClick={() => {
                dispatch(changePermissionRoleStatusRequest({
                  type: "deleted",
                  ids: selectedRoles.map((role) => role.role_id),
                  versions: selectedRoles.map((role) => role.version || 1),
                }));
              }}>Delete</Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="s">
          <Box variant="p">
            Are you sure you want to delete {selectedRoles.length === 1
              ? <>the role <strong>{selectedRoles[0]?.role_name}</strong></>
              : <><strong>{selectedRoles.length}</strong> selected roles</>
            }? Users assigned {selectedRoles.length === 1 ? "this role" : "these roles"} will lose their permissions.
          </Box>
          {affectedUsersForDelete.length > 0 && (
            <Alert type="warning">
              The following users are currently assigned {selectedRoles.length === 1 ? "this role" : "these roles"}:
              <Box margin={{ top: "xs" }}>
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {affectedUsersForDelete.map((email) => (
                    <li key={email}>{email}</li>
                  ))}
                </ul>
              </Box>
            </Alert>
          )}
        </SpaceBetween>
      </Modal>
    </div>
  );
};

export default RoleDefinitionsTab;
